import { Box, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { Icon } from '@iconify/react';
import { JSX, useEffect, useState } from 'react';
import { Roleplayer } from '../common/types/roleplayer';
import ClientList, { ClientListProps } from './clientList';

interface SelectClientsDialogProps extends ClientListProps {
  /**
   * Flag indicating if the dialog is open
   */
  open: boolean;
  /**
   * Callback for closing the dialog (with selected client ID if any)
   */
  onClose: (selectedClientIds?: number[]) => void;
  /**
   * Flag indicating if multiple clients can be selected
   * @default false
   */
  multiple?: boolean;
  /**
   * Dialog title
   * @default 'Choose client'
   */
  title?: string;
}

/**
 * Dialog for selecting clients
 */
const SelectClientsDialog = ({
  open,
  onClose,
  multiple = false,
  title = 'Choose client',
  ...clientListProps
}: SelectClientsDialogProps): JSX.Element => {
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);

  // If the dialog is closed, clear the selection
  useEffect(() => {
    if (!open) {
      setSelectedClientIds([]);
    }
  }, [open]);

  // If multiple selection is not allowed, close the dialog when a client is clicked
  const handleSelectClient = (selectedClient: Roleplayer): void => {
    const selectedClientId = selectedClient.id;
    if (multiple) {
      setSelectedClientIds((value) =>
        value.includes(selectedClientId)
          ? value.filter((id) => id !== selectedClientId)
          : [...value, selectedClientId],
      );
    } else {
      onClose([selectedClientId]);
    }
  };

  const handleClose = (): void => {
    onClose(selectedClientIds.length > 0 ? selectedClientIds : undefined);
  };

  return (
    <Dialog maxWidth="xl" fullWidth onClose={handleClose} open={open}>
      <Box className="flex justify-between items-start">
        <DialogTitle>{title}</DialogTitle>
        <IconButton onClick={handleClose}>
          <Icon icon="material-symbols:close" width={20} />
        </IconButton>
      </Box>
      <DialogContent>
        <ClientList
          {...clientListProps}
          validFirst
          selectedIds={selectedClientIds}
          onClickSelect={handleSelectClient}
        />
      </DialogContent>
    </Dialog>
  );
};

export default SelectClientsDialog;
